/**
 * The creature registry (doc 16 §2, creature lane). One row per species that
 * gets modeled — which, as of stage 7a, is all of them.
 *
 * Names, property markers and the depths a species can appear at all come from
 * `src/content/enemies.json` and are never retyped here: that is what
 * `statDrift` exists to prove, and it is the reason the Ironscale Ray cannot
 * quietly stop wearing plate bands when someone edits its armour value.
 *
 * `depths` in particular is DERIVED, not authored. A Broodmother at THE GLOOM
 * bursts into twelve Shelf krill and the Pale Lure calls six more, so krill are
 * checked for contrast against D2's floor as well as D1's — and no content edit
 * can strand that. Same reasoning as doc 06's codex popup deriving its carrier
 * list from the shoal roster rather than trusting an author to remember.
 */

import enemies from '../content/enemies.json' with { type: 'json' };
import { statesFor, type CreatureDraw, type CreatureSize, type CreatureSpec, type Markers } from './creature.ts';
import {
  drawGnasher,
  drawKrill,
  drawShellback,
  drawSilverdart,
  drawSnapjaw,
  drawTyrant,
} from './creatures/shelf.ts';
import {
  drawBroodmother,
  drawHagfish,
  drawIronscale,
  drawLanternjaw,
  drawLeviathan,
  drawPaleLure,
} from './creatures/gloom.ts';
import {
  drawChorister,
  drawIsopod,
  drawMother,
  drawPhantomEel,
  drawSovereign,
  drawThreadNode,
  drawTroglodont,
} from './creatures/floor.ts';

type Row = Record<string, unknown>;
const SPECIES_JSON = enemies.species as unknown as Record<string, Row>;

const DEPTH_ID = ['d1', 'd2', 'd3'] as const;

function depthIdOf(n: number): string {
  return DEPTH_ID[Math.max(0, Math.min(2, n - 1))]!;
}

/** Every species this one puts in the water: piñata, escort, threads, guard. */
function spawnsOf(row: Row): string[] {
  const out: string[] = [];
  const named = (v: unknown): void => {
    if (v && typeof v === 'object' && typeof (v as Row)['species'] === 'string') {
      out.push((v as Row)['species'] as string);
    }
  };
  named(row['onDeathSpawn']);
  named(row['threads']);
  named(row['guarded']);
  const escort = row['escort'];
  if (escort && typeof escort === 'object') {
    const spawn = (escort as Row)['spawn'];
    if (Array.isArray(spawn)) for (const e of spawn) named(e);
  }
  return out;
}

/**
 * The depths each species can be seen at: its own, plus every depth that puts
 * it in the water without being it. One pass, because nothing spawns a spawner.
 */
function appearanceDepths(): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const add = (id: string, depth: string): void => {
    const set = out.get(id) ?? new Set<string>();
    set.add(depth);
    out.set(id, set);
  };
  for (const [id, row] of Object.entries(SPECIES_JSON)) {
    const home = depthIdOf((row['depth'] as number | undefined) ?? 1);
    add(id, home);
    for (const child of spawnsOf(row)) add(child, home);
  }
  return out;
}

const DEPTHS_BY_ID = appearanceDepths();

function markersOf(row: Row): Markers {
  return {
    armored: ((row['armor'] as number | undefined) ?? 0) > 0,
    swift: (row['swift'] as boolean | undefined) ?? false,
    cloaked: (row['cloaked'] as boolean | undefined) ?? false,
    regen: ((row['regen'] as number | undefined) ?? 0) > 0,
  };
}

/** Build a row from content plus the three things art actually decides. */
function spec(
  id: string,
  size: CreatureSize,
  silhouetteFamily: string,
  read: string,
  draw: CreatureDraw,
): CreatureSpec {
  const row = SPECIES_JSON[id];
  if (!row) throw new Error(`no species '${id}' in enemies.json`);
  return {
    id,
    name: row['name'] as string,
    class: row['boss'] ? 'boss' : 'creature',
    size,
    silhouetteFamily,
    read,
    markers: markersOf(row),
    states: statesFor(
      (row['speed'] as number | undefined) ?? 0,
      ((row['dps'] as number | undefined) ?? 0) + ((row['rangedDps'] as number | undefined) ?? 0),
    ),
    depths: [...(DEPTHS_BY_ID.get(id) ?? new Set([depthIdOf(1)]))].sort(),
    reviewedAt: null,
    draw,
  };
}

export const BESTIARY: CreatureSpec[] = [
  // — THE SHELF ------------------------------------------------------------
  spec('krill', 'small', 'comma', 'a comma with a light on it', drawKrill),
  spec('gnasher', 'small', 'finned-wedge', 'the baseline fish, with a hinge at the front', drawGnasher),
  spec('shellback', 'medium', 'smooth-dome', 'a carapace on four legs, nothing above it', drawShellback),
  spec('silverdart', 'medium', 'needle', 'two rows of body and a point', drawSilverdart),
  spec('old_snapjaw', 'boss', 'jaw-on-legs', 'a solid mass with a hinge, walking', drawSnapjaw),
  spec('reef_tyrant', 'boss', 'split-wings', 'two triangles with a spine between them', drawTyrant),
  // — THE GLOOM ------------------------------------------------------------
  spec('lanternjaw', 'medium', 'stalked-lure', 'the only outline that reaches above AND ahead of itself', drawLanternjaw),
  spec('hagfish_spitter', 'medium', 'finless-tube', 'one continuous undulating line, no fins at all', drawHagfish),
  spec('ironscale_ray', 'large', 'solid-delta', 'one swept plate with a stinger', drawIronscale),
  spec('broodmother_jelly', 'large', 'trailing-bell', 'the only round top with everything hanging under it', drawBroodmother),
  spec('pale_lure', 'boss', 'arched-arm', 'pale bulk under a long arm ending in the light', drawPaleLure),
  spec('leviathan_calf', 'boss', 'slab-and-gape', 'a slab: no wings, no legs, no arm, just a mouth', drawLeviathan),
  // — THE FLOOR ------------------------------------------------------------
  spec('troglodont', 'large', 'humped-quadruped', 'a second mass stacked on its shoulders', drawTroglodont),
  spec('phantom_eel', 'large', 'fringed-ribbon', 'a line where the Hagfish is a tube', drawPhantomEel),
  spec('bulwark_isopod', 'large', 'segmented-barrel', 'legs down both sides and antennae out front', drawIsopod),
  spec('abyssal_chorister', 'large', 'standing-column', 'the only creature taller than it is wide', drawChorister),
  spec('thread_node', 'small', 'pegged-stalk', 'furniture: the only glow at the top of a stalk', drawThreadNode),
  spec('mother_of_threads', 'boss', 'trailing-tangle', 'six filaments that are also the fight', drawMother),
  spec('hadal_sovereign', 'boss', 'crowned-bulk', 'the only crown in the ocean', drawSovereign),
];

export function creatureById(id: string): CreatureSpec {
  const found = BESTIARY.find((c) => c.id === id);
  if (!found) throw new Error(`no creature spec: ${id}`);
  return found;
}

/** Ids the doc 16 pipeline draws. Anything missing here would render as nothing. */
export const BESTIARY_IDS = new Set(BESTIARY.map((c) => c.id));
